import { GoogleMap, Marker, InfoWindow, useLoadScript } from '@react-google-maps/api';
import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const geocodeAddress = async (address, apiKey) => {
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`;
  const response = await fetch(url);
  const data = await response.json();
  if (data.status === 'OK' && data.results[0]) {
    return data.results[0].geometry.location;
  }
  console.log(`Geocoding failed for address: ${address}, status: ${data.status}`);
  return null;
};

function StreetViewImage({ lat, lng, apiKey }) {
  const [error, setError] = useState(false);
  // Use a neutral heading and FOV for better coverage
  const url = `https://maps.googleapis.com/maps/api/streetview?size=220x100&location=${lat},${lng}&fov=90&heading=0&pitch=0&key=${apiKey}`;
  if (error) {
    return <div style={{width:'100%',height:100,display:'flex',alignItems:'center',justifyContent:'center',background:'#f1f5f9',borderRadius:6,marginBottom:8,fontSize:'0.95em',color:'#64748b'}}>No Street View available</div>;
  }
  return (
    <img
      src={url}
      alt="Street View"
      style={{ width: '100%', borderRadius: 6, marginBottom: 8, objectFit: 'cover', height: 100 }}
      onError={() => setError(true)}
    />
  );
}

const MapComponent = ({ projects, services, roster, apiKey }) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
  });
  const mapRef = useRef(null);
  const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.0060 });
  const [markers, setMarkers] = useState([]);
  const [activeMarker, setActiveMarker] = useState(null);

  useEffect(() => {
    if (!isLoaded || !projects || projects.length === 0) return;
    let cancelled = false;
    async function geocodeAll() {
      const results = [];
      for (const project of projects) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const loc = await geocodeAddress(project.address, apiKey);
          if (loc) results.push({ ...loc, project });
        } catch (e) {}
      }
      if (!cancelled) setMarkers(results);
    }
    geocodeAll();
    return () => { cancelled = true; };
  }, [isLoaded, projects, services, roster, apiKey]);

  // Fit map to markers when markers change
  useEffect(() => {
    if (!mapRef.current || markers.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    markers.forEach(marker => bounds.extend(marker));
    mapRef.current.fitBounds(bounds);
    if (markers.length === 1) setMapCenter(markers[0]);
  }, [markers]);

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <GoogleMap
      zoom={10}
      center={mapCenter}
      mapContainerStyle={{ width: '100%', height: '100%', minHeight: '300px' }}
      onLoad={map => { mapRef.current = map; }}
    >
      {markers.map((marker, idx) => (
        <Marker
          key={idx}
          position={{ lat: marker.lat, lng: marker.lng }}
          title={marker.address}
          onClick={() => setActiveMarker(idx)}
        />
      ))}
      {activeMarker !== null && markers[activeMarker] && (
        <InfoWindow
          position={{ lat: markers[activeMarker].lat, lng: markers[activeMarker].lng }}
          onCloseClick={() => setActiveMarker(null)}
        >
          <div style={{ maxWidth: 440, minWidth: 320 }}>
            <div style={{ fontWeight: 600 }}>{markers[activeMarker].project.Project_Name__c || ''}</div>
            <div style={{ fontSize: '0.95em', marginBottom: 6 }}>{markers[activeMarker].project.address}</div>
            <Link
              to={`/project/${markers[activeMarker].project.Id}`}
              style={{ color: '#2563eb', textDecoration: 'underline', fontWeight: 500, marginBottom: 8, display: 'inline-block' }}
              onClick={() => {
                const p = markers[activeMarker].project;
                localStorage.setItem('selectedProject', JSON.stringify({
                  ...p,
                  projectRoster: (roster || []).filter(rosterItem => rosterItem.Project_Profile__r.Id === p.Id),
                  projectServices: (services || []).filter(serviceItem => serviceItem.Project_Profile__r.Id === p.Id),
                  milrosePM: (p.Project_Manager__r && p.Project_Manager__r.Name) || (p.Owner && p.Owner.Name) || 'N/A',
                  milrosePMEmail: (p.Project_Manager__r && p.Project_Manager__r.Email) || (p.Owner && p.Owner.Email) || 'N/A',
                  milrosePMPhone: (p.Project_Manager__r && p.Project_Manager__r.Phone) || (p.Owner && p.Owner.Phone) || 'N/A',
                  arCoordinatorName: p.A_R_Coordinator_Full_Name__c,
                  arCoordinatorEmail: p.A_R_Coordinator_Email__c,
                  arCoordinatorPhone: p.A_R_Coordinator_Phone__c,
                  notes: p.Current_Status__c,
                  currentStatus: p.Project_Status__c,
                  groundUp: p.Ground_Up__c,
                  rollout: p.Rollout__c,
                  projectStatus: p.Project_Status__c,
                  clientName: (p.Billing_Company_Name__r && p.Billing_Company_Name__r.Name),
                }));
              }}
            >
              View Project Details
            </Link>
            <StreetViewImage
              lat={markers[activeMarker].lat}
              lng={markers[activeMarker].lng}
              apiKey={apiKey}
            />
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

export default MapComponent;
